// src/pages/OverviewClassic.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppShell from "../layout/AppShell";
import "./Dashboard.css";
import "../styles/buttons.css";
import { ENV, tbGetJson, tbPostJson, newReqId as clientNewReqId } from "../api/client";
import { useLookupReady } from "../hooks/useLookupReady";

/* ---------- helpers ---------- */
function makeCorrelationId() {
    try {
        if (typeof clientNewReqId === "function") {
            const v = clientNewReqId();
            if (v) return String(v);
        }
    } catch { }
    try {
        if (typeof window !== "undefined" && window.crypto?.randomUUID) {
            return window.crypto.randomUUID();
        }
    } catch { }
    return `req_${Date.now()}`;
}

function nowYm() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function labelFromYm(ym) {
    const [y, m] = ym.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(undefined, {
        month: "long",
        year: "numeric",
    });
}

const DASHBOARD_PAGE_SIZE = 1000;

/**
 * STATUS BUCKETING RULES (UPDATED):
 * - "Terminated" treated as Inactive
 */
const statusKey = (s = "") => {
    const t = String(s || "").toLowerCase();

    // bucket terminated with inactive
    if (t.includes("terminate") || t.includes("terminated")) return "inactive";

    if (t.includes("fail")) return "failed";

    if (t.includes("approve")) return "approved";
    if (t.includes("reject")) return "rejected";
    if (t.includes("draft")) return "draft";
    if (t.includes("info")) return "info";
    if (t.includes("inactive")) return "inactive";
    if (t.includes("active")) return "active";
    if (t.includes("pend") || t.includes("submit")) return "submitted";
    return "neutral";
};

/**
 * IMPORTANT FIX:
 * "inactive" contains "active", so includes("active") is wrong.
 * Use strict-ish classification for hinge personStatus.
 */
function normalizePersonStatus(s = "") {
    return String(s ?? "").trim().toLowerCase();
}
function classifyPersonStatus(s) {
    const t = normalizePersonStatus(s);
    if (!t) return "unknown";
    if (t === "terminated" || t.includes("terminate")) return "inactive";
    if (t === "active") return "active";
    if (t === "inactive") return "inactive";
    if (t.startsWith("active ")) return "active";
    if (t.startsWith("inactive ")) return "inactive";
    return "unknown";
}
const isActive = (s = "") => classifyPersonStatus(s) === "active";
const isInactive = (s = "") => classifyPersonStatus(s) === "inactive";

const STATUS_RANK = {
    failed: 6,
    approved: 5,
    active: 4,
    submitted: 4,
    info: 3,
    draft: 2,
    rejected: 1,
    inactive: 0,
    neutral: 0,
};

function parseDateToStamp(v) {
    if (!v) return NaN;
    const mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(v);
    if (mdy) return Date.UTC(+mdy[3], +mdy[1] - 1, +mdy[2]);
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    if (iso) return Date.UTC(+iso[1], +iso[2] - 1, +iso[3]);
    const t = Date.parse(v);
    return Number.isNaN(t) ? NaN : t;
}

const workerText = (r) =>
    r.worker || `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim();
const vendorText = (r) =>
    r.vendor ||
    r.vendor_name ||
    r.npw_manager_company ||
    r.supplier_reference ||
    r.supplierName ||
    "-";
const contractText = (r) =>
    r.contract ?? r.contract_number ?? r.contract_no ?? r.contractNumber ?? "-";
const roleText = (r) => {
    const v =
        r.role ??
        r.roleName ??
        r.role_name ??
        r.userRole ??
        r.user_role ??
        r.loggedInUserRole ??
        r.roleType ??
        r.role_type ??
        "";
    const s = String(v ?? "").trim();
    return s || "-";
};
const poText = (r) => r.po ?? r.po_number ?? r.po_no ?? r.poNumber ?? "-";
const lineText = (r) =>
    r.line ?? r.line_number ?? r.line_no ?? r.lineNumber ?? "-";

function getVendor() {
    const vendorId = sessionStorage.getItem("tb_vendorId") || "";
    const supplierName = sessionStorage.getItem("tb_supplierName") || "";
    return (vendorId || supplierName || "").trim();
}

/* ---------- TB aggregated ---------- */
async function apiGetAggregated(ym) {
    const base = (ENV.TB_AGG_URL || "").replace(/\/$/, "");
    if (!base) throw new Error("TB_AGG_URL not configured");

    const storedRole = (sessionStorage.getItem("tb_role") || "Nominator").trim();
    const email = sessionStorage.getItem("tb_email") || "";
    const dtvId = sessionStorage.getItem("tb_dtvid") || "";
    const vendor = getVendor();
    const sponsorId = (email || dtvId || email || "").trim();

    const params = new URLSearchParams();
    params.set("month", ym);
    params.set("role", storedRole);
    params.set("loggedInUserRole", storedRole);

    if (storedRole.toLowerCase() === "nominator") {
        if (vendor) params.set("vendor", vendor);
    } else if (storedRole.toLowerCase() === "employee sponsor") {
        if (sponsorId) params.set("sponsor", sponsorId);
    }

    if (dtvId) params.set("id", dtvId);

    const url = `${base}?${params.toString()}`;
    return tbGetJson(url);
}

/* ---------- Notifications ---------- */
async function apiGetNotifications() {
    const base = (ENV.TB_NOTIF_URL || "").replace(/\/$/, "");
    if (!base) return [];

    const storedRole = (sessionStorage.getItem("tb_role") || "Nominator").trim();
    const email = (sessionStorage.getItem("tb_email") || "").trim();
    const dtvId = (sessionStorage.getItem("tb_dtvid") || "").trim();
    const vendor = getVendor();
    const sponsorId = (email || dtvId || email || "").trim();

    const params = new URLSearchParams();
    params.set("role", storedRole);
    params.set("loggedInUserRole", storedRole);

    const roleKey = storedRole.toLowerCase();

    if (roleKey === "nominator") {
        if (vendor) params.set("vendor", vendor);
        if (dtvId) params.set("id", dtvId);
        if (email) params.set("email", email);
    } else if (roleKey === "employee supplier admin") {
        if (email || dtvId) params.set("id", dtvId || email || dtvId);
        if (email) params.set("email", email);
    } else if (roleKey === "fte nominator") {
        if (dtvId || email) params.set("id", dtvId || dtvId || email);
        if (dtvId || email) params.set("email", email || dtvId || email);
    } else if (roleKey === "employee sponsor") {
        if (sponsorId) params.set("sponsor", sponsorId);
        if (dtvId) params.set("id", dtvId);
    } else if (roleKey === "cp channel delegate") {
        if (dtvId) params.set("id", dtvId);
        if (email) params.set("email", email);
    } else {
        const id = (dtvId || email || dtvId || "").trim();
        if (id) params.set("id", id);
    }

    const url = `${base}?${params.toString()}`;
    try {
        return await tbGetJson(url);
    } catch {
        return [];
    }
}

/* ---------- TB-proxied Hinge person/list ---------- */
function buildHingeSearchCriteria(storedRole, month, dtvId, supplierName) {
    const roleKey = (storedRole || "").toLowerCase();
    if (roleKey === "nominator") {
        return { roleName: "Nominator", userVendorName: supplierName || "", month };
    }
    if (roleKey === "employee supplier admin") {
        // return { roleName: "Employee Supplier Admin", nominatorDtvId: dtvId || "", month }; // changed done or commented of new requirement
        return { roleName: "Employee Supplier Admin", month };

    }
    if (roleKey === "fte nominator") {
        return { roleName: "FTE Nominator", nominatorDtvId: dtvId || "", month };
    }
    if (roleKey === "employee sponsor") {
        return { roleName: "Employee Sponsor", approverDtvId: dtvId || "", month };
    }
    if (roleKey === "cp channel delegate") {
        return {
            roleName: "CP Channel Delegate",
            isCPChannelDelegate: true,
            attuidApproverDtvId: dtvId || "",
            month
        };
    }
    if (roleKey === "dtv ops admin" || (!storedRole && !dtvId)) {
        return { roleName: storedRole || "DTV OPS Admin", month };
    }
    return { roleName: storedRole, month };
}

async function apiGetHingePersonList(ym) {
    if (!ENV.ENABLE_HINGE) return { personList: [], totalRecordCount: 0 };

    const url = (ENV.TB_HINGE_PERSON_LIST_URL || "").trim();
    if (!url) return { personList: [], totalRecordCount: 0 };

    const storedRole = (sessionStorage.getItem("tb_role") || "").trim();
    const dtvId = sessionStorage.getItem("tb_dtvid") || "";
    const supplierName = (sessionStorage.getItem("tb_supplierName") || getVendor()).trim();

    const searchCriteria = buildHingeSearchCriteria(storedRole, ym, dtvId, supplierName);

    const body = {
        correlationId: makeCorrelationId(),
        sourceSystemCode: "1",
        searchCriteria,
        // pagination: { pageNumber: "1", pageSize: String(ENV.PAGE_SIZE_DEFAULT || 25) },
        pagination: { pageNumber: "1", pageSize: String(DASHBOARD_PAGE_SIZE) },
    };

    try {
        const res = await tbPostJson(url, body);

        // Your proxy returns: { upstreamStatus, response: { totalRecordCount, personList } }
        const payload =
            res?.response ||
            res?.data?.response ||
            res?.body?.response ||
            res?.data ||
            res?.body ||
            res ||
            {};

        const list = Array.isArray(payload?.personList) ? payload.personList : [];

        return {
            personList: list,
            totalRecordCount: Number.parseInt(payload?.totalRecordCount, 10) || list.length || 0,
        };
    } catch {
        return { personList: [], totalRecordCount: 0 };
    }
}

/* ---------- constants ---------- */
const RETURN_PATH_KEY = "npw.search.returnPath";
const OPEN_FLAG = "npw.search.reopen";
const OVERVIEW_CLASSIC_STATE_KEY = "tb.overviewClassic.state.v1";

function loadOverviewClassicState() {
    try {
        const raw = sessionStorage.getItem(OVERVIEW_CLASSIC_STATE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
        return null;
    }
}

function saveOverviewClassicState(state) {
    try {
        sessionStorage.setItem(OVERVIEW_CLASSIC_STATE_KEY, JSON.stringify(state));
    } catch {
        /* ignore */
    }
}

/* ---------- role route groups ---------- */
const ROLE_APPROVAL = new Set(["employee supplier admin", "employee sponsor", "cp channel delegate"]);
const ROLE_MANAGE = new Set([
    "nominator",
    "fte nominator",
    // "employee supplier admin",
    "dtv ops admin",
]);

/* =================================================================== */
export default function OverviewClassic() {
    const navigate = useNavigate();
    const location = useLocation();
    const savedState = loadOverviewClassicState();

    const storedRole = (sessionStorage.getItem("tb_role") || "Nominator").trim();
    const userRole = storedRole.toLowerCase();
    const showVendor = userRole !== "nominator";
    // Role column shown only for CP Channel Delegate logged-in role
    const showRole = userRole === "cp channel delegate";

    const monthKey = useMemo(() => nowYm(), []);
    const monthLabel = useMemo(() => labelFromYm(monthKey), [monthKey]);

    const [vendorReady, setVendorReady] = useState(false);
    // Wait for the lookup API to finish loading before firing hinge-list / npw APIs.
    // Applies to every role — no role checks.
    const lookupReady = useLookupReady();
    useEffect(() => {
        if (!lookupReady) {
            setVendorReady(false);
            return;
        }
        if (userRole !== "nominator") {
            setVendorReady(true);
            return;
        }
        const id = setInterval(() => {
            if (getVendor()) setVendorReady(true);
        }, 200);
        return () => clearInterval(id);
    }, [userRole, lookupReady]);

    const [summary, setSummary] = useState({
        total: 0,
        failed: 0,
        approved: 0,
        pending: 0,
        drafts: 0,
        rejected: 0,
    });

    const [rows, setRows] = useState([]);
    const [activeCount, setActiveCount] = useState(0);
    const [inactiveCount, setInactiveCount] = useState(0);

    const [page, setPage] = useState(savedState?.page && Number(savedState.page) > 0 ? Number(savedState.page) : 1);
    const PAGE_SIZE_KEY = "tb_overviewclassic_pageSize";
    const getInitialPageSize = () => {
        try {
            const savedPageSize = Number(savedState?.pageSize);
            if ([5, 10, 25, 50].includes(savedPageSize)) return savedPageSize;
            const raw =
                sessionStorage.getItem(PAGE_SIZE_KEY) ||
                (typeof localStorage !== "undefined" ? localStorage.getItem(PAGE_SIZE_KEY) : null);
            const n = parseInt(String(raw || ""), 10);
            if ([5, 10, 25, 50].includes(n)) return n;
        } catch { }
        return 5;
    };

    const [pageSize, setPageSize] = useState(getInitialPageSize);

    useEffect(() => {
        try {
            sessionStorage.setItem(PAGE_SIZE_KEY, String(pageSize));
            if (typeof localStorage !== "undefined") {
                localStorage.setItem(PAGE_SIZE_KEY, String(pageSize));
            }
        } catch { }
    }, [pageSize]);
    const [notifs, setNotifs] = useState([]);

    // Notifications rail (collapsible)

    const [showNotifs, setShowNotifs] = useState(() => {
        if (typeof savedState?.showNotifs === "boolean") return savedState.showNotifs;
        try {
            return sessionStorage.getItem("tb_show_notifs") === "true";
        } catch {
            return false;
        }
    });
    const [loading, setLoading] = useState(true);

    const [sortKey, setSortKey] = useState(typeof savedState?.sortKey === "string" ? savedState.sortKey : "start");
    const [sortDir, setSortDir] = useState(savedState?.sortDir === "asc" ? "asc" : "desc");
    const [query, setQuery] = useState("");

    /**
     * KPI FILTER + SORT
        * - total | failed | pending | drafts | rejected | active | inactive
     * - pendingItems (local-only view: submitted + draft)
     */
    const [kpiFocus, setKpiFocus] = useState(typeof savedState?.kpiFocus === "string" ? savedState.kpiFocus : "total");

    /**
     * Click behavior:
     * - Clicking a KPI sets filter to that KPI.
     * - Clicking the same KPI toggles sort direction desc <-> asc.
     * - Switching KPI resets to default sort (desc).
     */
    const lastKpiRef = useRef({ focus: typeof savedState?.kpiFocus === "string" ? savedState.kpiFocus : null });
    const didHydrateFilterRef = useRef(false);
    function onKpiClick(nextFocus) {
        setKpiFocus(nextFocus);

        // If same KPI clicked again: just toggle sort direction.
        if (lastKpiRef.current.focus === nextFocus) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
            setPage(1);
            return;
        }

        // New KPI: default sort setup
        lastKpiRef.current.focus = nextFocus;

        if (nextFocus === "total") {
            setSortKey("start");
            setSortDir("desc");
        } else {
            // Most KPI filters should sort by status rank primarily.
            setSortKey("status");
            setSortDir("desc");
        }
        setPage(1);
    }

    const leftColRef = useRef(null);
    const syncRailRef = useRef(null);
    const [railBox, setRailBox] = useState({ left: 0, width: 0 });
    useEffect(() => {
        function syncRail() {
            const el = leftColRef.current;
            if (!el) return;
            const r = el.getBoundingClientRect();

            // Clamp to viewport so the fixed pager never overflows when rails collapse/expand.
            const pad = 12;
            const viewportLeft = window.scrollX;
            const viewportRight = window.scrollX + window.innerWidth;

            let width = Math.max(0, r.width);
            width = Math.min(width, Math.max(0, window.innerWidth - pad * 2));

            let left = r.left + window.scrollX;
            left = Math.max(viewportLeft + pad, Math.min(left, viewportRight - width - pad));

            setRailBox({ left, width });
        }
        syncRailRef.current = syncRail;
        syncRail();
        window.addEventListener("resize", syncRail);
        window.addEventListener("scroll", syncRail, { passive: true });
        let ro;
        if ("ResizeObserver" in window && leftColRef.current) {
            ro = new ResizeObserver(syncRail);
            ro.observe(leftColRef.current);
        }
        return () => {
            window.removeEventListener("resize", syncRail);
            window.removeEventListener("scroll", syncRail);
            if (ro) ro.disconnect();
        };
    }, []);
    useEffect(() => {
        try {
            // Ensure pager rail width stays correct when layout changes (notif rail / focus mode)
            const fn = syncRailRef.current;
            if (typeof fn === "function") setTimeout(fn, 0);
        } catch { }
    }, [showNotifs]);

    // persist UI prefs
    useEffect(() => {
        try { sessionStorage.setItem("tb_show_notifs", String(showNotifs)); } catch { }
    }, [showNotifs]);

    useEffect(() => {
        lastKpiRef.current.focus = kpiFocus;
    }, [kpiFocus]);

    useEffect(() => {
        saveOverviewClassicState({
            page,
            pageSize,
            sortKey,
            sortDir,
            kpiFocus,
            showNotifs,
        });
    }, [page, pageSize, sortKey, sortDir, kpiFocus, showNotifs]);

    /* ---------- load data ---------- */
    useEffect(() => {
        if (!vendorReady) return;
        let cancel = false;

        (async () => {
            try {
                setLoading(true);

                const [aggRes, notifRes, hingeRes] = await Promise.allSettled([
                    apiGetAggregated(monthKey),
                    apiGetNotifications(),
                    apiGetHingePersonList(monthKey),
                ]);

                // TB aggregated
                let aggPipeline = [];
                let aggSummary = { total: 0, failed: 0, approved: 0, pending: 0, drafts: 0, rejected: 0 };
                if (aggRes.status === "fulfilled") {
                    const agg = aggRes.value || {};
                    aggPipeline = Array.isArray(agg.pipeline) ? agg.pipeline : [];
                    aggSummary = { ...aggSummary, ...(agg.summary || {}) };

                    aggPipeline = aggPipeline.map((r) => ({
                        ...r,
                        _source: "agg",
                        _routeId:
                            r.npw_id ??
                            r.npwId ??
                            r.request_id ??
                            r.client_request_id ??
                            r.id ??
                            "",
                        start: r.start || r.start_date,
                        end: r.end || r.end_date,
                    }));

                    // If backend doesn't provide failed in summary, compute it from pipeline rows.
                    if (!Number.isFinite(Number(aggSummary.failed))) {
                        aggSummary.failed = 0;
                    }
                    if (Number(aggSummary.failed) === 0) {
                        const failedFromPipeline = aggPipeline.filter((r) => statusKey(r.status) === "failed").length;
                        if (failedFromPipeline > 0) aggSummary.failed = failedFromPipeline;
                    }
                }

                // TB-proxied Hinge list (Active/Inactive counts)
                let hingeList = [];
                let aCount = 0;
                let iCount = 0;
                if (hingeRes.status === "fulfilled") {
                    const list = Array.isArray(hingeRes.value?.personList)
                        ? hingeRes.value.personList
                        : [];

                    hingeList = list.map((p) => {
                        const status = p.personStatus;
                        const cls = classifyPersonStatus(status);

                        if (cls === "active") aCount += 1;
                        else if (cls === "inactive") iCount += 1;

                        return {
                            contractNumber: p.contractNumber ?? p.contract ?? null,
                            poNumber: p.poNumber ?? p.po ?? null,
                            lineNumber: p.lineNumber ?? p.line ?? null,
                            first_name: p.firstName,
                            last_name: p.lastName,
                            supplierName: p.supplierName,
                            status, // keep raw personStatus
                            start: p.effectiveStartDate,
                            end: p.effectiveEndDate,
                            _source: "hinge",
                            _routeId: p.dtvAnchorId || p.attuid || p.primaryEmail || "",
                            _dtvid: p.dtvAnchorId || p.attuid || p.primaryEmail || "",
                        };
                    });
                }

                const merged = [...aggPipeline, ...hingeList];

                const notifList =
                    notifRes.status === "fulfilled" && Array.isArray(notifRes.value)
                        ? notifRes.value.slice(0, 5)
                        : [];

                if (!cancel) {
                    setRows(merged);
                    setSummary(aggSummary);
                    setActiveCount(aCount);
                    setInactiveCount(iCount);
                    setNotifs(notifList);
                }
            } finally {
                if (!cancel) setLoading(false);
            }
        })();

        return () => {
            cancel = true;
        };
    }, [monthKey, vendorReady]);

    /* ---------- search input ---------- */
    useEffect(() => {
        const handler = (e) => setQuery(String(e.detail ?? "").trim());
        window.addEventListener("tb:search", handler);
        return () => window.removeEventListener("tb:search", handler);
    }, []);

    useEffect(() => {
        if (!didHydrateFilterRef.current) {
            didHydrateFilterRef.current = true;
            return;
        }
        setPage(1);
    }, [query, kpiFocus]);

    /* ---------- KPI FILTER (THIS IS THE MAIN FIX) ---------- */
    const kpiFilteredRows = useMemo(() => {
        const base = rows;

        // Special local-only view
        if (kpiFocus === "pendingItems") {
            return base.filter((r) => {
                const k = statusKey(r.status);
                return k === "submitted" || k === "draft" || k === "info" || k === "offboarding submitted to cp" || k === "offboarding submitted";
            });
        }

        if (kpiFocus === "total") return base;

        // Active/Inactive should work for hinge and any agg rows that happen to have those words.
        if (kpiFocus === "active") {
            return base.filter((r) => {
                // hinge rows: use classifyPersonStatus
                if (r._source === "hinge") return classifyPersonStatus(r.status) === "active";
                // agg rows: use statusKey
                return statusKey(r.status) === "active";
            });
        }
        if (kpiFocus === "inactive") {
            return base.filter((r) => {
                if (r._source === "hinge") return classifyPersonStatus(r.status) === "inactive";
                return statusKey(r.status) === "inactive";
            });
        }

        // Failed / Drafts / Rejected are straightforward
        if (kpiFocus === "failed") {
            return base.filter((r) => statusKey(r.status) === "failed");
        }
        if (kpiFocus === "drafts") {
            return base.filter((r) => statusKey(r.status) === "draft");
        }
        if (kpiFocus === "rejected") {
            return base.filter((r) => statusKey(r.status) === "rejected");
        }

        // Pending: treat Submitted + Info Requested as "pending bucket"
        if (kpiFocus === "pending") {
            return base.filter((r) => {
                const k = statusKey(r.status);
                return k === "submitted" || k === "info" || k === "offboarding submitted to cp" || k === "offboarding submitted";
            });
        }

        return base;
    }, [rows, kpiFocus]);

    /* ---------- search filter ---------- */
    const filtered = useMemo(() => {
        const base = kpiFilteredRows;

        const q = query.toLowerCase();
        if (!q) return base;

        return base.filter((r) => {
            const bits = [
                workerText(r),
                vendorText(r),
                contractText(r),
                poText(r),
                lineText(r),
                r.status,
                r.start || "",
                r.end || "",
            ]
                .join(" ")
                .toLowerCase();
            return bits.includes(q);
        });
    }, [kpiFilteredRows, query]);

    /* ---------- sort ---------- */
    const sorted = useMemo(() => {
        const dir = sortDir === "asc" ? 1 : -1;
        const list = [...filtered];

        list.sort((a, b) => {
            // If status sort, we want the KPI's status type to surface first in DESC:
            // rank already does that. For active/inactive, hinge rows might not map rank;
            // statusKey will map "active" / "inactive" properly as strings.
            const cmpText = (x, y) =>
                String(x || "").localeCompare(String(y || ""), undefined, {
                    sensitivity: "base",
                });

            if (sortKey === "worker") return dir * cmpText(workerText(a), workerText(b));
            if (sortKey === "vendor") return dir * cmpText(vendorText(a), vendorText(b));
            if (sortKey === "role") return dir * cmpText(roleText(a), roleText(b));
            if (sortKey === "contract") return dir * cmpText(contractText(a), contractText(b));
            if (sortKey === "po") return dir * cmpText(poText(a), poText(b));
            if (sortKey === "line") return dir * cmpText(lineText(a), lineText(b));

            if (sortKey === "status") {
                const ra = STATUS_RANK[statusKey(a.status)] ?? 0;
                const rb = STATUS_RANK[statusKey(b.status)] ?? 0;
                if (ra !== rb) return dir * (ra - rb);

                // tie-breaker: start date (most recent first when desc)
                const ta = parseDateToStamp(a.start);
                const tb = parseDateToStamp(b.start);
                if (!Number.isNaN(ta) && !Number.isNaN(tb)) return dir * (ta - tb);

                return dir * cmpText(workerText(a), workerText(b));
            }

            if (sortKey === "end") {
                const ta = parseDateToStamp(a.end);
                const tb = parseDateToStamp(b.end);
                if (!Number.isNaN(ta) && !Number.isNaN(tb)) return dir * (ta - tb);
                if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
                return Number.isNaN(ta) ? dir * -1 : dir * 1;
            }

            // default: start
            const ta = parseDateToStamp(a.start);
            const tb = parseDateToStamp(b.start);
            if (!Number.isNaN(ta) && !Number.isNaN(tb)) return dir * (ta - tb);
            if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
            return Number.isNaN(ta) ? dir * -1 : dir * 1;
        });

        return list;
    }, [filtered, sortKey, sortDir]);

    const totalPages = Math.max(1, Math.ceil(sorted.length / (pageSize || 5)));
    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const paged = useMemo(
        () =>
            sorted.slice(
                (page - 1) * (pageSize || 5),
                (page - 1) * (pageSize || 5) + (pageSize || 5)
            ),
        [sorted, page, pageSize]
    );

    function toggleSort(key) {
        // manual header sort overrides current sortKey; still respects KPI filter
        if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else {
            setSortKey(key);
            setSortDir("asc");
            setPage(1);
        }
    }

    function rememberHere() {
        try {
            const here =
                (typeof window !== "undefined" && window.location?.pathname) ||
                location.pathname ||
                "/overview";
            sessionStorage.setItem(RETURN_PATH_KEY, here);
            sessionStorage.removeItem(OPEN_FLAG);
        } catch { }
    }

    function getDtvid(r) {
        return (
            r._dtvid ||
            r.dtv_id ||
            r.dtvId ||
            r.dtvAnchorId ||
            r.attuid ||
            r.primaryEmail ||
            r._routeId ||
            ""
        );
    }

    function openRow(r) {
        rememberHere();

        const roleIsApprovals = ROLE_APPROVAL.has(userRole);
        const goApprovals = roleIsApprovals;

        const ai = isActive(r.status) || isInactive(r.status);

        if (ai) {
            const dtvid = getDtvid(r);
            if (!dtvid) return;
            const url = goApprovals
                ? `/approvals/DTVID=${encodeURIComponent(String(dtvid))}`
                : `/manage-requests/DTVID=${encodeURIComponent(String(dtvid))}`;
            navigate(url);
            return;
        }

        const npwId = r._routeId;
        if (!npwId) return;

        // Drafts should always open the onboarding edit screen (all roles)
        if (statusKey(r.status) === "draft") {
            const from = `${location.pathname || ""}${location.search || ""}${location.hash || ""}` || "/";
            navigate(`/onboarding/new/${encodeURIComponent(String(npwId))}`, { state: { from } });
            return;
        }

        const url = goApprovals
            ? `/approvals/NPWID=${encodeURIComponent(String(npwId))}`
            : `/manage-requests/NPWID=${encodeURIComponent(String(npwId))}`;
        navigate(url);
    }

    function openNotification(n) {
        rememberHere();

        const roleIsApprovals = ROLE_APPROVAL.has(userRole);
        const goApprovals = roleIsApprovals;

        const titleText =
            n.title || n.message || n.text || n.subject || n.notificationTitle || "";

        const notifStatus =
            n.status || n.personStatus || n.currentStatus || n.state || "";

        const isDraftNotif =
            statusKey(notifStatus) === "draft" ||
            String(titleText).toLowerCase().includes("draft");

        const ai = isActive(notifStatus) || isInactive(notifStatus);

        const pickDtvid = () =>
            n._dtvid ||
            n.dtvId ||
            n.dtv_id ||
            n.dtvAnchorId ||
            n.attuid ||
            n.primaryEmail ||
            n.workerDtvId ||
            n.workerId ||
            n.personId ||
            "";

        const pickNpwId = () =>
            n.npwId ||
            n.npw_id ||
            n.requestId ||
            n.client_request_id ||
            n.reqId ||
            // Some notif feeds use id as notification id, not NPW id.
            // Keep it as a last-resort fallback only.
            n.id ||
            "";

        if (ai) {
            const dtvid = pickDtvid();
            if (!dtvid) return;
            const url = goApprovals
                ? `/approvals/DTVID=${encodeURIComponent(String(dtvid))}`
                : `/manage-requests/DTVID=${encodeURIComponent(String(dtvid))}`;
            navigate(url);
            return;
        }

        const npwId = pickNpwId();
        const inferNpwIdFromText = () => {
            const s = String(titleText || "");
            if (!s) return "";

            const m1 = s.match(/\bNPW\s*ID\s*[:=#]?\s*(\d{1,12})\b/i);
            if (m1?.[1]) return m1[1];
            const m2 = s.match(/\bNPWID\s*[:=#]?\s*(\d{1,12})\b/i);
            if (m2?.[1]) return m2[1];
            const m3 = s.match(/\brequest\s*(?:id|#)?\s*[:=#]?\s*(\d{1,12})\b/i);
            if (m3?.[1]) return m3[1];
            const m4 = s.match(/\/onboarding\/new\/(\d{1,12})\b/i);
            if (m4?.[1]) return m4[1];

            return "";
        };

        const finalNpwId = npwId || inferNpwIdFromText();
        if (!finalNpwId) return;

        // Draft notifications should always open the onboarding edit screen (all roles)
        if (isDraftNotif) {
            const from = `${location.pathname || ""}${location.search || ""}${location.hash || ""}` || "/";
            navigate(`/onboarding/new/${encodeURIComponent(String(finalNpwId))}`, { state: { from } });
            return;
        }

        const url = goApprovals
            ? `/approvals/NPWID=${encodeURIComponent(String(finalNpwId))}`
            : `/manage-requests/NPWID=${encodeURIComponent(String(finalNpwId))}`;
        navigate(url);
    }

    /* ---------- layout styles ---------- */
    const GRID_LINE = "#e5e7eb";
    const cellPad = { padding: "8px 10px", fontSize: 12, lineHeight: 1.25 };

    const tdTrunc = {
        ...cellPad,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        borderBottom: `1px solid ${GRID_LINE}`,
    };

    const thBase = {
        ...cellPad,
        background: "#fff",
        fontWeight: 700,
        borderBottom: `1px solid ${GRID_LINE}`,
        whiteSpace: "normal",
        wordBreak: "word-break",
    };

    const tdStatus = { ...tdTrunc, paddingLeft: 4 };
    const tdDate = { ...tdTrunc, paddingLeft: 4, paddingRight: 4 };
    const thStatus = { ...thBase, paddingLeft: 10 };
    const thDate = { ...thBase, paddingLeft: 10 };

    const COLS_NO_VENDOR = {
        contract: "13%",
        po: "11%",
        line: "7%",
        worker: "28%",
        role: "16%",
        stat: "10%",
        start: "15.5%",
        end: "15.5%",
    };

    const COLS_WITH_VENDOR = {
        contract: "9%",
        po: "12%",
        line: "6%",
        worker: "26%",
        vendor: "14%",
        role: "12%",
        stat: "9%",
        start: "12%",
        end: "12%",
    };

    const COLS = showVendor ? COLS_WITH_VENDOR : COLS_NO_VENDOR;

    const kpisRowStyle = {
        display: "grid",
        gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
        gap: 12,
        alignItems: "stretch",
    };
    const kpiBtnStyle = {
        padding: "10px 12px",
        minWidth: 0,
        border: "2px solid transparent",
        transition: "border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease, transform 60ms ease",
    };
    const kpiActiveStyle = {
        borderColor: "#2563eb",
        boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.18)",
        background: "#eff6ff",
    };
    const kpiLabelSm = { fontSize: 12, lineHeight: 1.15 };
    const kpiValueSm = { fontSize: 20, lineHeight: 1.1 };

    const totalWithHinge = (summary.total || 0) + activeCount + inactiveCount;
    const notifCount = loading ? 0 : Math.min(5, Array.isArray(notifs) ? notifs.length : 0);

    const tableTitle = useMemo(() => {
        if (kpiFocus === "pendingItems") return "Pending Items:";
        if (kpiFocus === "total") return "Onboarding Pipeline (All):";
        if (kpiFocus === "failed") return "Failed Requests:";
        if (kpiFocus === "pending") return "Pending Items:";
        if (kpiFocus === "drafts") return "Draft Requests:";
        if (kpiFocus === "rejected") return "Rejected Requests:";
        if (kpiFocus === "active") return "Active Workers (Hinge List + Any Active Rows):";
        if (kpiFocus === "inactive") return "Inactive Workers (Inactive + Terminated):";
        return "Onboarding Pipeline:";
    }, [kpiFocus]);

    const getKpiStyle = (focusKey) => ({
        ...kpiBtnStyle,
        ...(kpiFocus === focusKey ? kpiActiveStyle : null),
    });

    /* Remove any "vendor: ..." in rendered notification titles */
    function formatNotifTitle(n) {
        const raw = n?.title ?? n?.message ?? n?.text ?? n?.subject ?? "";
        if (!raw) return "";
        return String(raw)
            .replace(/\s*\(\s*vendor\s*:\s*null\s*\)/gi, "")
            .replace(/\s*,?\s*vendor\s*:\s*null/gi, "")
            .replace(/\s*\(\s*from\s*:\s*(?:null|undefined)\s*\)/gi, "")
            .replace(/\s*,?\s*from\s*:\s*(?:null|undefined)/gi, "")
            .replace(/\bfrom\b\s*(?:null|undefined)\b/gi, "")
            .replace(/\bfrom\b\s*$/gi, "")
            .replace(/\s{2,}/g, " ")
            .replace(/[,\s]+$/g, "")
            .trim();
    }

    return (
        <AppShell>
            <div className={`top-progress ${loading ? "show" : ""}`} />

            <div className="dash-grid" style={{ gridTemplateColumns: showNotifs ? "minmax(0,1fr) 280px" : "minmax(0,1fr)" }}>
                {/* LEFT */}
                <div className="left-col" ref={leftColRef}>
                    <div className="actions centered" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                        <button className="btn-navy" onClick={() => navigate("/onboarding/new")}>
                            Start New Onboarding
                        </button>

                        {/* Pending Items local view */}
                        <button
                            className="btn-navy"
                            aria-pressed={kpiFocus === "pendingItems"}
                            onClick={() => {
                                setKpiFocus("pendingItems");
                                setSortKey("status");
                                setSortDir("desc");
                                lastKpiRef.current.focus = "pendingItems";
                                setPage(1);
                            }}
                            style={kpiFocus === "pendingItems" ? { boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.22)", border: "2px solid #2563eb" } : undefined}
                        >
                            View Pending Items
                        </button>

                        <div style={{ flex: 1 }} />

                        <button
                            type="button"
                            onClick={() => setShowNotifs((v) => !v)}
                            style={{
                                position: "relative",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 40,
                                height: 40,
                                borderRadius: 999,
                                border: "1px solid #e5e7eb",
                                background: "#fff",
                                cursor: "pointer",
                            }}
                            aria-label={showNotifs ? "Hide notifications" : "Show notifications"}
                            title={showNotifs ? "Hide notifications" : "Show notifications"}
                        >
                            <span className="bell-ico" aria-hidden="true">🔔</span>
                            {!loading && notifCount > 0 && (
                                <span
                                    style={{
                                        position: "absolute",
                                        top: -4,
                                        right: -6,
                                        minWidth: 16,
                                        height: 16,
                                        padding: "0 5px",
                                        borderRadius: 999,
                                        background: "#f87171",
                                        color: "#ffffff",
                                        fontSize: 10,
                                        fontWeight: 700,
                                        lineHeight: "16px",
                                        textAlign: "center",
                                        boxShadow: "0 0 0 2px #ffffff",
                                        pointerEvents: "none",
                                    }}
                                    aria-label={`Notifications count ${notifCount}`}
                                    title={`${notifCount}`}
                                >
                                    {notifCount}
                                </span>
                            )}
                        </button>
                    </div>

                    <div className="month-block">
                        <div className="month-label">Viewing data for: {monthLabel}</div>
                        <div className="kpis" style={kpisRowStyle}>
                            {loading ? (
                                <>
                                    <KpiSkeleton /><KpiSkeleton /><KpiSkeleton />
                                    <KpiSkeleton /><KpiSkeleton /><KpiSkeleton /><KpiSkeleton />
                                </>
                            ) : (
                                <>
                                    <button type="button" onClick={() => onKpiClick("total")} className="kpi" style={getKpiStyle("total")} aria-pressed={kpiFocus === "total"}>
                                        <div>
                                            <div className="kpi-label" style={kpiLabelSm}>Total Requests</div>
                                            <div className="kpi-value" style={{ ...kpiValueSm, color: "#334155" }}>{totalWithHinge}</div>
                                        </div>
                                    </button>
                                    <button type="button" onClick={() => onKpiClick("failed")} className="kpi" style={getKpiStyle("failed")} aria-pressed={kpiFocus === "failed"}>
                                        <div>
                                            <div className="kpi-label" style={kpiLabelSm}>Failed</div>
                                            <div className="kpi-value" style={{ ...kpiValueSm, color: "#b91c1c" }}>{Number(summary.failed || 0)}</div>
                                        </div>
                                    </button>
                                    <button type="button" onClick={() => onKpiClick("pending")} className="kpi" style={getKpiStyle("pending")} aria-pressed={kpiFocus === "pending"}>
                                        <div>
                                            <div className="kpi-label" style={kpiLabelSm}>Pending</div>
                                            <div className="kpi-value" style={{ ...kpiValueSm, color: "#b45309" }}>{summary.pending}</div>
                                        </div>
                                    </button>
                                    <button type="button" onClick={() => onKpiClick("drafts")} className="kpi" style={getKpiStyle("drafts")} aria-pressed={kpiFocus === "drafts"}>
                                        <div>
                                            <div className="kpi-label" style={kpiLabelSm}>Drafts</div>
                                            <div className="kpi-value" style={{ ...kpiValueSm, color: "#334155" }}>{summary.drafts}</div>
                                        </div>
                                    </button>
                                    <button type="button" onClick={() => onKpiClick("rejected")} className="kpi" style={getKpiStyle("rejected")} aria-pressed={kpiFocus === "rejected"}>
                                        <div>
                                            <div className="kpi-label" style={kpiLabelSm}>Rejected</div>
                                            <div className="kpi-value" style={{ ...kpiValueSm, color: "#b91c1c" }}>{summary.rejected}</div>
                                        </div>
                                    </button>
                                    <button type="button" onClick={() => onKpiClick("active")} className="kpi" style={getKpiStyle("active")} aria-pressed={kpiFocus === "active"}>
                                        <div>
                                            <div className="kpi-label" style={kpiLabelSm}>Active</div>
                                            <div className="kpi-value" style={{ ...kpiValueSm, color: "#2563eb" }}>{activeCount}</div>
                                        </div>
                                    </button>
                                    <button type="button" onClick={() => onKpiClick("inactive")} className="kpi" style={getKpiStyle("inactive")} aria-pressed={kpiFocus === "inactive"}>
                                        <div>
                                            <div className="kpi-label" style={kpiLabelSm}>Inactive</div>
                                            <div className="kpi-value" style={{ ...kpiValueSm, color: "#64748b" }}>{inactiveCount}</div>
                                        </div>
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Unified Pipeline Table */}
                    <div className="table-card">
                        <div className="table-title">{tableTitle}</div>
                        <div className="table-wrap">
                            <table>
                                <colgroup>
                                    {/* <col style={{ width: COLS.contract }} /> */}
                                    {/* <col style={{ width: COLS.po }} /> */}
                                    {/* <col style={{ width: COLS.line }} /> */}
                                    <col style={{ width: COLS.worker }} />
                                    {showVendor && <col style={{ width: COLS.vendor }} />}
                                    {showRole && <col style={{ width: COLS.role }} />}
                                    <col style={{ width: COLS.stat }} />
                                    <col style={{ width: COLS.start }} />
                                    <col style={{ width: COLS.end }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        {/* <th style={thBase} onClick={() => toggleSort("contract")} className="sortable">Contract</th> */}
                                        {/* <th style={thBase} onClick={() => toggleSort("po")} className="sortable">PO</th> */}
                                        {/* <th style={thBase} onClick={() => toggleSort("line")} className="sortable">Line</th> */}
                                        <th style={thBase} onClick={() => toggleSort("worker")} className="sortable">Worker Name</th>
                                        {showVendor && (
                                            <th style={thBase} onClick={() => toggleSort("vendor")} className="sortable">Vendor</th>
                                        )}
                                        {showRole && (
                                            <th style={thBase} onClick={() => toggleSort("role")} className="sortable">Role</th>
                                        )}
                                        <th style={thStatus} onClick={() => toggleSort("status")} className="sortable">Status</th>
                                        <th style={thDate} onClick={() => toggleSort("start")} className="sortable">Start Date</th>
                                        <th style={thDate} onClick={() => toggleSort("end")} className="sortable">End Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paged.map((r, i) => {
                                        const bg = i % 2 ? "#EEEEEE" : "#fff";
                                        const nameFull =
                                            r.worker ||
                                            [r.first_name, r.last_name].filter(Boolean).join(" ").trim() ||
                                            "-";
                                        const stKey = statusKey(r.status);
                                        return (
                                            <tr key={`${r._source}-${r._routeId}-${i}`} style={{ background: bg }}>
                                                {/* <td style={tdTrunc} title={String(contractText(r))}>{contractText(r)}</td> */}
                                                {/* <td style={tdTrunc} title={String(poText(r))}>{poText(r)}</td> */}
                                                {/* <td style={tdTrunc} title={String(lineText(r))}>{lineText(r)}</td> */}
                                                <td style={tdTrunc} title={nameFull}>
                                                    <button
                                                        type="button"
                                                        className="sec-pill sec-pill-link"
                                                        onClick={() => openRow(r)}
                                                        title={nameFull}
                                                    >
                                                        {nameFull}
                                                    </button>
                                                </td>
                                                {showVendor && (
                                                    <td style={tdTrunc} title={String(vendorText(r))}>{vendorText(r)}</td>
                                                )}
                                                {showRole && (
                                                    <td style={tdTrunc} title={String(roleText(r))}>{roleText(r)}</td>
                                                )}
                                                {/*<td style={tdStatus } title={String(r.status)}> */}
                                                <td style={{ ...tdStatus, paddingRight: 6 }} title={String(r.status)}>

                                                    <span className={`status-pill ${stKey}`}>
                                                        {stKey === "info" ? "Info Requested" : r.status || "-"}
                                                    </span>
                                                </td>
                                                <td style={tdDate} title={String(r.start)}>{r.start || "-"}</td>
                                                <td style={tdDate} title={String(r.end)}>{r.end || "-"}</td>
                                            </tr>
                                        );
                                    })}
                                    {!loading && paged.length === 0 && (
                                        <tr>
                                            <td colSpan={(showVendor ? 8 : 7) + (showRole ? 1 : 0)} style={{ textAlign: "center", color: "#6b7280", padding: 12 }}>
                                                No rows
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pager */}
                    {sorted.length > 0 && (
                        <div
                            className="pager-rail-fixed"
                            style={{
                                position: "fixed",
                                left: railBox.left,
                                width: railBox.width,
                                maxWidth: "100vw",
                                boxSizing: "border-box",
                                bottom: "calc(var(--footer-h) + 28px)",
                                zIndex: 40,
                            }}
                            aria-live="polite"
                        >
                            <div className="pager-rail-inner" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", width: "100%", boxSizing: "border-box" }}>
                                <span className="pg-status" style={{ flex: "1 1 auto", minWidth: 140 }}>
                                    Page {page} of {Math.max(1, Math.ceil(sorted.length / (pageSize || 5)))}
                                </span>
                                <div className="pager-buttons" style={{ display: "flex", gap: 10, flexWrap: "nowrap" }}>
                                    <label className="pg-rowsize" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ fontSize: 12, color: "#64748b" }}>Rows:</span>
                                        <select
                                            value={pageSize}
                                            onChange={(e) => {
                                                const v = Number(e.target.value || 5);
                                                setPageSize(v);
                                                setPage(1);
                                            }}
                                            style={{
                                                height: 34,
                                                borderRadius: 10,
                                                border: "1px solid #e5e7eb",
                                                padding: "0 10px",
                                                background: "#fff",
                                                fontSize: 13,
                                            }}
                                        >
                                            {[5, 10, 25, 50].map((n) => (
                                                <option key={n} value={n}>
                                                    {n}
                                                </option>
                                            ))}
                                        </select>
                                    </label>

                                    <button
                                        type="button"
                                        className="pg"
                                        disabled={page <= 1}
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    >
                                        ◀ Previous
                                    </button>
                                    <button
                                        type="button"
                                        className="pg"
                                        disabled={page >= Math.max(1, Math.ceil(sorted.length / (pageSize || 5)))}
                                        onClick={() =>
                                            setPage((p) =>
                                                Math.min(Math.max(1, Math.ceil(sorted.length / (pageSize || 5))), p + 1)
                                            )
                                        }
                                    >
                                        Next ▶
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* RIGHT rail (Notifications) */}
                {showNotifs && (
                    <div className="right-col">
                        <div className="panel notif-panel">
                            <div className="panel-hdr" style={{ display: "flex", alignItems: "center", gap: 10 }}>

                                <span className="notif-h">Notifications</span>
                                <button
                                    type="button"
                                    onClick={() => setShowNotifs(false)}
                                    style={{
                                        marginLeft: "auto",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: 34,
                                        height: 34,
                                        borderRadius: 10,
                                        border: "1px solid #e5e7eb",
                                        background: "#fff",
                                        cursor: "pointer",
                                    }}
                                    aria-label="Close notifications"
                                    title="Close notifications"
                                >
                                    ✕
                                </button>

                            </div>

                            <ul className="notif-list">
                                {notifs.map((n, idx) => (
                                    <li key={n.id ?? idx}>
                                        <div className="dot" />
                                        <div>
                                            <div className="n-title">
                                                <a
                                                    href="#"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        openNotification(n);
                                                    }}
                                                    title={formatNotifTitle(n)}
                                                >
                                                    {formatNotifTitle(n)}
                                                </a>
                                            </div>
                                            <div className="n-ago">{n.ago}</div>
                                        </div>
                                    </li>
                                ))}
                                {!notifs?.length && !loading && <li className="empty">No notifications</li>}
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        </AppShell>
    );
}

function KpiSkeleton() {
    return (
        <div className="kpi kpi-skeleton" style={{ padding: "10px 12px" }}>
            <div className="kpi-skel-inner">
                <span className="skeleton-line" />
                <span className="skeleton-bubble mt-10" />
            </div>
        </div>
    );
}


